import React, { useContext, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Button } from 'primereact/button';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import SvgBell from '@icons/bell.svg?react';

interface ComponentProps {
  companyId: string;
}

const NotificationCenterSection: React.FC<ComponentProps> = ({ companyId }) => {
  const { tr } = useTranslationLgs();
  const { adminReadAllCompanyNotifications } = useContext(ApiContext);
  const { showToastMessage } = useContext(ToastContext);
  const [readAllNotificationsVisible, setReadAllNotificationsVisible] = useState(false);
  const [readingAllNotifications, setReadingAllNotifications] = useState(false);
  /** How many notifications the last run marked as read — null until the action has been used. */
  const [readAllNotificationsCount, setReadAllNotificationsCount] = useState<number | null>(null);

  // Marks every notification of the company as read, for all of its users — asked for from support
  // when a company's notification center is flooded. Independent of the form, so it runs immediately.
  const readAllNotifications = () => {
    setReadingAllNotifications(true);
    adminReadAllCompanyNotifications(
      companyId,
      data => {
        setReadingAllNotifications(false);
        setReadAllNotificationsVisible(false);
        setReadAllNotificationsCount(data?.updatedCount ?? 0);
        showToastMessage(
          tr('generic.done', 'Done'),
          tr('adminCompanyDetail.readAllNotificationsDone', 'Marked {{count}} notification as read', { count: data?.updatedCount ?? 0 }, data?.updatedCount ?? 0),
          'success',
        );
      },
      undefined,
      {
        onError: (_error, showStandardError) => {
          setReadingAllNotifications(false);
          showStandardError();
        },
      },
    );
  };

  return (
    <SectionPanel
      icon={<SvgBell className={'w-6 h-6'} />}
      iconColor={'var(--content-tertiary)'}
      heading={tr('adminCompanyDetail.sectionNotificationCenter', 'Notification center')}
      dataTestId={'admin-company-section-notifications'}>
      <HorizontalFieldValue
        label={tr('adminCompanyDetail.readAllNotifications', 'Unread notifications')}
        value={
          <div className={'flex flex-row items-center gap-4'}>
            <Button
              label={tr('adminCompanyDetail.readAllNotificationsAction', 'Mark all as read')}
              onClick={() => setReadAllNotificationsVisible(true)}
              loading={readingAllNotifications}
              disabled={readingAllNotifications}
            />
            {readAllNotificationsCount !== null && (
              <span className={'text-content-secondary'}>
                {tr('adminCompanyDetail.readAllNotificationsDone', 'Marked {{count}} notification as read', { count: readAllNotificationsCount }, readAllNotificationsCount)}
              </span>
            )}
          </div>
        }
      />
      <ConfirmDialog
        visible={readAllNotificationsVisible}
        dialogTitle={tr('adminCompanyDetail.readAllNotificationsTitle', 'Mark all notifications as read')}
        dialogMessage={tr(
          'adminCompanyDetail.readAllNotificationsConfirm',
          'This marks every unread notification of this company as read, for all of its users. It cannot be undone.',
        )}
        onAcceptButtonText={tr('adminCompanyDetail.readAllNotificationsAction', 'Mark all as read')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
        loading={readingAllNotifications}
        onConfirm={readAllNotifications}
        onCancel={() => setReadAllNotificationsVisible(false)}
      />
    </SectionPanel>
  );
};

export default NotificationCenterSection;
